import debounce from 'lodash.debounce'
import { html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { RaygunElement } from '@src/js/common/rg-element.js'
import { waitFor } from '@src/js/common/wait-for'
import mapStyle from './map-style.json'

@customElement('rg-map')
export class RgMap extends RaygunElement {

  @query('.map-container')
  mapElement = null

  // 'roadmap' | 'satellite' | 'hybrid' | 'terrain'
  @property({ type: String })
  type = 'roadmap'

  @property({ type: Number })
  zoom = 8

  @property({ type: Number, attribute: 'max-zoom' })
  maxZoom = 16

  @property({ type: Number, attribute: 'min-zoom' })
  minZoom = 5

  @property({ type: String, attribute: 'api-key' })
  apiKey = ''

  @property({ type: Number, attribute: 'center-lat' })
  centerLat = '32.7123856'

  @property({ type: Number, attribute: 'center-lng' })
  centerLng = '-117.1625987'

  @property({ type: Boolean, attribute: 'fit-to-markers' })
  fitToMarkers = false

  @property({ type: Boolean, attribute: 'hide-default-ui' })
  hideDefaultUI = false

  @property({ type: Boolean, attribute: 'hide-fullscreen' })
  hideFullscreen = false

  @property({ type: Boolean, attribute: 'hide-zoom' })
  hideZoom = false

  @property({ type: Boolean, attribute: 'hide-street-view' })
  hideStreetView = false

  @property({ type: Boolean, attribute: 'hide-map-type' })
  hideMapType = false

  @property({ type: Boolean, attribute: 'hide-scale' })
  hideScale = false

  @state({ type: Object })
  observer = null

  @state({ type: Object })
  map = null

  @state({ type: Object })
  infoWindow = null

  @state({ type: Array })
  markers = []

  @state({ type: Number })
  markersZoom = 8

  registerEvents() {
    return {
      'marker-register': this.onContentChanged
    }
  }

  scripLoaded() {
    return global.google && global.google.maps
  }

  initMap() {
    this.map = new global.google.maps.Map(this.mapElement, {
      styles: mapStyle,
      center: { lat: Number(this.centerLat), lng: Number(this.centerLng) },
      zoom: this.zoom,
      minZoom: this.minZoom,
      maxZoom: this.maxZoom,
      mapTypeId: this.type,
      disableDefaultUI: this.hideDefaultUI,
      fullscreenControl: !this.hideFullscreen && !this.hideDefaultUI,
      mapTypeControl: !this.hideMapType && !this.hideDefaultUI,
      zoomControl: !this.hideZoom && !this.hideDefaultUI,
      streetViewControl: !this.hideStreetView && !this.hideDefaultUI,
      scaleControl: !this.hideScale && !this.hideDefaultUI,
    })
    global.google.maps.event.addListener(this.map, 'click', this.onMapClick)
  }

  getMarkerByPosition(lat, lng) {
    return this.markers.find(m => m.position.lat() === Number(lat) && m.position.lng() === Number(lng))
  }

  getElementPosition(el) {
    return [
      Number(el.getAttribute('lat')),
      Number(el.getAttribute('lng'))
    ]
  }

  async centerToMarker(marker) {
    return new Promise(resolve => {
      if (marker) {
        global.google.maps.event.addListenerOnce(this.map, 'idle', resolve)
        this.map.panTo(marker.position)
      } else {
        resolve()
      }
    })
  }

  async zoomToMarker(marker) {
    return new Promise(resolve => {
      global.google.maps.event.addListenerOnce(this.map, 'idle', resolve)
      this.map.setZoom(this.markersZoom + 1)
    })
  }

  openInfoWindow(markerElement, anchor) {
    const hasInfoWindow = markerElement.slot('infowindow').length > 0
    if (!hasInfoWindow) return
    if (!this.infoWindow) {
      this.infoWindow = new global.google.maps.InfoWindow({
        map: this.map,
      })
      this.infoWindow.addListener('closeclick', this.closeInfoWindow)
    }
    const d = document.createElement('div')
    d.append(...markerElement.slot('infowindow'))
    this.infoWindow.setContent(d.innerHTML)
    this.infoWindow.open({ anchor })
    this.infoWindow.focus()
  }

  closeInfoWindow = () => {
    if (this.infoWindow) {
      this.infoWindow.close()
    }
    this.fitMapToMarkers()
  }

  animateMarker(marker, timeSeconds) {
    this.markers.forEach(m => m.setAnimation(null))
    marker.setAnimation(global.google.maps.Animation.BOUNCE)
    setTimeout(() => marker.setAnimation(null), timeSeconds * 1000)
  }

  setActiveMarker(marker) {
    const lat = marker ? marker.position.lat() : 0
    const lng = marker ? marker.position.lng() : 0

    this.markers.forEach(m => {
      m.setZIndex(0)
      m.setIcon(m === marker ? m.activeIcon : m.normalIcon)
    })
    if (marker) {
      marker.setZIndex(1)
      this.animateMarker(marker, 2)
    }
    this.querySelectorAll('rg-map-marker').forEach(el => {
      const [ eLat, eLng ] = this.getElementPosition(el)
      if (eLat === lat && eLng === lng) {
        el.setAttribute('selected', true)
        setTimeout(() => {
          this.openInfoWindow(el, marker)
        })
      } else {
        el.removeAttribute('selected')
      }
    })
    if (!marker) {
      this.closeInfoWindow()
    }
  }

  onMapClick = () => {
    this.setActiveMarker(null)
  }

  onMarkerClick = async (e) => {
    const { lat, lng } = e.latLng
    const marker = this.getMarkerByPosition(lat(), lng())
    this.dispatchEvent(new CustomEvent('marker-click', {
      bubbles: true,
      detail: {
        marker,
      },
    }))
    await this.centerToMarker(marker)
    await this.zoomToMarker(marker)
    this.setActiveMarker(marker)
  }

  async selectMarkerFromPosition(lat, lng) {
    const marker = this.getMarkerByPosition(lat, lng)
    if (marker) {
      await this.centerToMarker(marker)
      await this.zoomToMarker(marker)
      this.setActiveMarker(marker)
    }
  }

  initMarkers() {
    const removedMarkers = new Set(this.markers)
    this.markers = Array.from(this.querySelectorAll('rg-map-marker')).map(el => {
      const [lat, lng] = this.getElementPosition(el)
      const position = new global.google.maps.LatLng(lat, lng)
      const prevMarker = this.getMarkerByPosition(lat, lng)
      if (prevMarker) {
        removedMarkers.delete(prevMarker)
        return prevMarker
      }

      const normalIcon = el.getAttribute('icon')
      const activeIcon = el.getAttribute('icon-active') || normalIcon

      const marker = new global.google.maps.Marker({
        normalIcon,
        activeIcon,
        position,
        opacity: 1,
        map: this.map,
        animation: global.google.maps.Animation.DROP,
        title: el.title,
        icon: normalIcon,
      })
      marker.addListener('click', this.onMarkerClick)
      return marker
    })

    removedMarkers.forEach(marker => {
      marker.setMap(null)
      marker.unbindAll()
    })
  }

  async fitMapToMarkers() {
    if (!this.map || !this.fitToMarkers) return
    if (!this.markers.length) return
    const bounds = new global.google.maps.LatLngBounds()
    this.markers.forEach(marker => bounds.extend(marker.position))
    this.map.fitBounds(bounds)
    return new Promise(resolve => {
      global.google.maps.event.addListenerOnce(this.map, 'idle', () => {
        this.markersZoom = this.map.zoom
        resolve()
      })
    })
  }

  async injectScript() {
    if (this.scripLoaded()) return true
    if (!global.document.querySelector('script[rg-map-script]')) {
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}`
      script.async = true
      script.defer = true
      script.setAttribute('rg-map-script', true)
      document.body.appendChild(script)
    }
    return await waitFor(this.scripLoaded)
  }

  async firstUpdated() {
    if (await this.injectScript()) {
      this.initMap()
      this.onContentChanged()
    }
  }

  onContentChanged = debounce(async () => {
    await waitFor(() => this.map !== null)
    this.initMarkers()
    this.setActiveMarker(null)
    this.fitMapToMarkers()
  }, 100)

  render() {
    return html`
      ${this.slot()}
      <div class="map-container"></div>
    `;
  }
}
